import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Card,
  Switch,
  FormControlLabel
} from '@mui/material';
import Grid from '../components/common/Grid'; // Using custom Grid component
import { ProfileFormValues, PasswordFormValues } from '../types/forms';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import AuthService from '../services/auth.service';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ minHeight: '520px', height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// User details schema
const ProfileSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  organization: Yup.string().required('Organization is required'),
  description: Yup.string(),
});

// Password change schema
const PasswordChangeSchema = Yup.object().shape({
  old_password: Yup.string().required('Current password is required'),
  new_password: Yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('new_password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const AccountPage: React.FC = () => {
  const { currentUser, updateUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentSuccess, setConsentSuccess] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState<boolean>(currentUser?.consent_accepted || false);

  // Sync consent state with current user
  useEffect(() => {
    if (currentUser) {
      setConsentAccepted(currentUser.consent_accepted || false);
    }
  }, [currentUser]);

  if (!currentUser) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5">
            Please log in to view your account
          </Typography>
        </Paper>
      </Container>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle profile update
  const handleProfileUpdate = async (values: ProfileFormValues, { setSubmitting }: FormikHelpers<ProfileFormValues>) => {
    try {
      setProfileError(null);
      const response = await AuthService.updateDetails({
        ...values,
        email: currentUser.email,
      });

      if (response.status === 'details_updated') {
        setProfileSuccess('Your profile has been updated successfully');
        updateUser({
          ...currentUser,
          first_name: values.first_name,
          last_name: values.last_name,
          organization: values.organization,
          description: values.description,
        });
      }
    } catch (err: any) {
      if (err.response && err.response.data) {
        setProfileError(err.response.data.detail || 'Profile update failed');
      } else {
        setProfileError('An error occurred. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (
    values: PasswordFormValues,
    { setSubmitting, resetForm }: FormikHelpers<PasswordFormValues>
  ) => {
    try {
      setPasswordError(null);
      const { confirm_password, ...passwordData } = values;

      const response = await AuthService.changePassword(passwordData);

      if (response.status === 'password_updated') {
        setPasswordSuccess('Your password has been updated successfully');
        resetForm();
      }
    } catch (err: any) {
      if (err.response && err.response.data) {
        setPasswordError(err.response.data.detail || 'Password change failed');
      } else {
        setPasswordError('An error occurred. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle consent update
  const handleConsentUpdate = async () => {
    try {
      setConsentError(null);
      const response = await AuthService.updateConsent(currentUser!.email, consentAccepted);

      if (response.status === 'consent_updated') {
        setConsentSuccess('Your consent preferences have been updated successfully');
        updateUser({
          ...currentUser!,
          consent_accepted: consentAccepted,
          consent_viewed: true,
        });
      }
    } catch (err: any) {
      if (err.response && err.response.data) {
        setConsentError(err.response.data.detail || 'Consent update failed');
      } else {
        setConsentError('An error occurred. Please try again later.');
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <AccountCircleIcon sx={{ mr: 2, fontSize: '2.5rem', color: '#68c8de' }} />
        <Typography variant="h4" component="h1">
          My Account
        </Typography>
      </Box>

      <Card sx={{ backgroundColor: 'rgba(18, 18, 18, 0.8)', borderRadius: 2, mb: 4, minHeight: '720px' }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: '#68c8de',
                color: '#121212',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}
            >
              {currentUser.first_name?.[0]}{currentUser.last_name?.[0]}
            </Avatar>

            <Box sx={{ ml: 2 }}>
              <Typography variant="h6">
                {currentUser.first_name} {currentUser.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentUser.organization || 'No organization'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          <Box sx={{ mt: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="account tabs"
              sx={{
                minHeight: '48px',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#68c8de'
                },
                '& .MuiTab-root': {
                  minHeight: '48px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  padding: '12px 16px'
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#68c8de'
                }
              }}
            >
              <Tab label="Profile" id="tab-0" aria-controls="tabpanel-0" />
              <Tab label="Change Password" id="tab-1" aria-controls="tabpanel-1" />
              <Tab label="Privacy & Consent" id="tab-2" aria-controls="tabpanel-2" />
            </Tabs>
          </Box>
        </Box>

        {/* PROFILE TAB */}
        <TabPanel value={tabValue} index={0}>
          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {profileError}
            </Alert>
          )}

          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {profileSuccess}
            </Alert>
          )}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Personal Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Update your personal profile information below
            </Typography>
          </Box>

          <Formik
            initialValues={{
              email: currentUser.email,
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              organization: currentUser.organization || '',
              description: currentUser.description || '',
            }}
            validationSchema={ProfileSchema}
            onSubmit={handleProfileUpdate}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Grid container spacing={3} sx={{ py: 1 }}>
                  {/* ROW 1: Email (read-only display) */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Email Address
                    </Typography>
                    <Typography variant="body1">
                      {currentUser.email}
                    </Typography>
                  </Grid>

                  {/* ROW 2: First + Last Name */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      First Name
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="first_name"
                      error={touched.first_name && Boolean(errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Last Name
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="last_name"
                      error={touched.last_name && Boolean(errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>

                  {/* ROW 3: Organization */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Organization
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="organization"
                      error={touched.organization && Boolean(errors.organization)}
                      helperText={touched.organization && errors.organization}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  {/* Divider after organization field */}
                  <Grid item xs={12} sx={{ my: 2 }}>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
                  </Grid>
                  
                  {/* ROW 4: Description */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="description"
                      multiline
                      rows={3}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>

                  {/* Divider after description field */}
                  <Grid item xs={12} sx={{ my: 2 }}>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
                  </Grid>
                  
                  {/* ROW 5: Update Profile button (its own distinct row) */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                          borderRadius: 1,
                          textTransform: 'none',
                          fontWeight: 400,
                          py: 1.5,
                          px: 3,
                          backgroundColor: '#68c8de',
                          '&:hover': { backgroundColor: '#4ba9bf' }
                        }}
                      >
                        {isSubmitting ? 'Saving...' : 'Update My Profile'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </TabPanel>

        {/* PASSWORD TAB */}
        <TabPanel value={tabValue} index={1}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}

          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Ensure your account is secure with a strong password
            </Typography>
          </Box>

          <Formik
            initialValues={{
              email: currentUser.email,
              old_password: '',
              new_password: '',
              confirm_password: '',
            }}
            validationSchema={PasswordChangeSchema}
            onSubmit={handlePasswordChange}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Grid container spacing={3} sx={{ py: 1 }}>
                  {/* ROW 1: Current password */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current Password
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="old_password"
                      type="password"
                      error={touched.old_password && Boolean(errors.old_password)}
                      helperText={touched.old_password && errors.old_password}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Divider after current password */}
                  <Grid item xs={12} sx={{ my: 2 }}>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
                  </Grid>

                  {/* ROW 2: New Password */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      New Password
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="new_password"
                      type="password"
                      error={touched.new_password && Boolean(errors.new_password)}
                      helperText={touched.new_password && errors.new_password}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Divider between password fields */}
                  <Grid item xs={12} sx={{ my: 2 }}>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
                  </Grid>
                  
                  {/* ROW 3: Confirm password */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Confirm New Password
                    </Typography>
                    <Field
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      name="confirm_password"
                      type="password"
                      error={touched.confirm_password && Boolean(errors.confirm_password)}
                      helperText={touched.confirm_password && errors.confirm_password}
                      InputProps={{
                        sx: {
                          borderRadius: 1,
                          backgroundColor: 'rgba(30, 30, 30, 0.6)',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.15)'
                          }
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Divider before button */}
                  <Grid item xs={12} sx={{ my: 2 }}>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
                  </Grid>
                  
                  {/* ROW 4: Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                          borderRadius: 1,
                          textTransform: 'none',
                          fontWeight: 400,
                          py: 1.5,
                          px: 3,
                          backgroundColor: '#68c8de',
                          '&:hover': { backgroundColor: '#4ba9bf' }
                        }}
                      >
                        {isSubmitting ? 'Changing...' : 'Change Password'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </TabPanel>

        {/* CONSENT TAB */}
        <TabPanel value={tabValue} index={2}>
          {consentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {consentError}
            </Alert>
          )}

          {consentSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {consentSuccess}
            </Alert>
          )}

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Data Collection Consent
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Manage your preferences for data collection and usage in EcoActionAI.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Current Status: <strong>{consentAccepted ? 'Data collection enabled' : 'Data collection disabled'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              When enabled, EcoActionAI may collect usage data to improve our services. You can change this preference at any time. Details of this are available at <a href="https://www.csiro.au/Privacy" target="_blank" rel="noopener noreferrer">https://www.csiro.au/Privacy</a>.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                  color="primary"
                />
              }
              label="Allow data collection for service improvement"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleConsentUpdate}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 400,
                py: 1.5,
                px: 3,
                backgroundColor: '#68c8de',
                '&:hover': { backgroundColor: '#4ba9bf' }
              }}
            >
              Update Preferences
            </Button>
          </Box>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default AccountPage;